import { useState, useEffect } from "react"
import { Loader2, Medal, Search, Trophy } from "lucide-react"
import CoachSidebar from "../components/CoachSidebar"
import EmptyState from "../components/EmptyState"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function CoachResults() {
  const [results, setResults] = useState([])
  const [search, setSearch] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  useEffect(() => {
    async function fetchResults() {
      setIsLoading(true)
      try {
        const res = await fetch(`${API}/api/coach/swimmers/results`, { headers: authHeaders() })
        if (!res.ok) throw new Error("Failed to fetch results")
        setResults(await res.json())
      } catch (e) {
        setError(e.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchResults()
  }, [])

  const filtered = results.filter(r => 
    r.event_title.toLowerCase().includes(search.toLowerCase()) || 
    r.swimmer_name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <CoachSidebar />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10 relative">
        <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("coach_results_title")}</h1>
            <p className="mt-1 text-sm text-mist-300">{t("coach_results_subtitle")}</p>
          </div>
        </header>

        {error && <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}

        <div className="mb-6 flex items-center gap-4">
          <div className="flex flex-1 items-center gap-3 rounded-lg border border-white/10 bg-ink-800/50 px-4 py-2.5">
            <Search className="h-4 w-4 text-mist-500" />
            <input 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              placeholder={t("coach_results_search")}
              className="bg-transparent text-sm text-paper-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60 rounded flex-1"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold-400" /></div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-ink-800/30 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-white/10 bg-ink-800/80 text-xs font-semibold uppercase tracking-wider text-mist-400">
                <tr>
                  <th className="px-6 py-4">{t("coach_results_col_rank")}</th>
                  <th className="px-6 py-4">{t("coach_results_col_swimmer")}</th>
                  <th className="px-6 py-4">{t("coach_results_col_event")}</th>
                  <th className="px-6 py-4">{t("coach_results_col_cat")}</th>
                  <th className="px-6 py-4 text-right">{t("coach_results_col_time")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map(r => (
                  <tr key={r.id} className="transition-colors hover:bg-white/[0.02]">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {r.rank === 1 && <Medal className="h-5 w-5 text-yellow-400" />}
                        {r.rank === 2 && <Medal className="h-5 w-5 text-slate-300" />}
                        {r.rank === 3 && <Medal className="h-5 w-5 text-amber-600" />}
                        {r.rank > 3 && <span className="ml-1 w-4 text-center font-bold text-mist-500">{r.rank}</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-paper-0">{r.swimmer_name}</td>
                    <td className="px-6 py-4 text-mist-300">{r.event_title}</td>
                    <td className="px-6 py-4">
                      <span className="rounded bg-white/5 px-2 py-1 text-[10px] font-semibold text-mist-400">
                        {r.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-gold-400">{r.finish_time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {filtered.length === 0 && (
              <EmptyState
                icon={<Trophy className="h-12 w-12 text-mist-600" />}
                title={t("coach_results_empty")}
                hint={search ? t("coach_results_empty_hint1") : t("coach_results_empty_hint2")}
              />
            )}
          </div>
        )}
      </main>
    </div>
  )
}
